/**
 * AudioTab: 설정 다이얼로그 > 오디오 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/AudioTab
 * Redux connect → useAppSelector/useAppDispatch 전환
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { localActions } from '@/store/slices/localSlice';
import testSound from '@/assets/media/liveStreamingOn.mp3';

let audioContext: AudioContext | null = null;
let javascriptNode: ScriptProcessorNode | null = null;

interface AudioTabProps {
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const AudioTab: React.FC<AudioTabProps> = ({ onChangeDevice }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);

  const audioOutputDeviceId = local.audioOutputDeviceId;
  const audioInputDeviceId = local.audioInputDeviceId;

  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);

  const [isMicDropdownOpen, setIsMicDropdownOpen] = useState(false);
  const [isSpeakerDropdownOpen, setIsSpeakerDropdownOpen] = useState(false);

  const micDropdownRef = useRef<HTMLDivElement>(null);
  const speakerDropdownRef = useRef<HTMLDivElement>(null);

  const [micLevel, setMicLevel] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const speakerVolume = local.speakerVolume;

  const isMuteOnJoin = local.isMuteOnJoin;
  const isSpaceUnmute = local.isSpaceUnmute;

  // 오디오 디바이스 가져오기
  const loadAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMicDevices(devices.filter((d) => d.kind === 'audioinput'));
      setSpeakerDevices(devices.filter((d) => d.kind === 'audiooutput'));
    } catch (err) {
      console.warn('[AudioTab] enumerateDevices failed:', err);
    }
  }, []);

  useEffect(() => {
    loadAudioDevices();
    navigator.mediaDevices.addEventListener('devicechange', loadAudioDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadAudioDevices);
    };
  }, [loadAudioDevices]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (micDropdownRef.current && !micDropdownRef.current.contains(e.target as Node)) {
        setIsMicDropdownOpen(false);
      }
      if (speakerDropdownRef.current && !speakerDropdownRef.current.contains(e.target as Node)) {
        setIsSpeakerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 스피커 디바이스 변경 시 setSinkId
  useEffect(() => {
    if (audioRef.current && audioOutputDeviceId) {
      try {
        if (typeof (audioRef.current as any).setSinkId === 'function') {
          (audioRef.current as any).setSinkId(audioOutputDeviceId);
        }
      } catch (err) {
        console.warn('setSinkId fail:', err);
      }
    }
  }, [audioOutputDeviceId]);

  // 마이크 레벨 감지
  const micProcess = useCallback((deviceId: string) => {
    if (javascriptNode && audioContext) {
      javascriptNode.disconnect(audioContext.destination);
    }
    setMicLevel(0);

    navigator.mediaDevices
      .getUserMedia({ audio: { deviceId: { exact: deviceId } } })
      .then((stream) => {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.5;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(javascriptNode);
        javascriptNode.connect(audioContext.destination);

        javascriptNode.onaudioprocess = () => {
          const array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          let values = 0;
          for (let i = 0; i < array.length; i++) {
            values += array[i];
          }
          const average = (values / array.length) * 2;
          const level = Math.floor((average / 100) * 88);
          setMicLevel(level > 88 ? 88 : level);
        };
      })
      .catch((err) => {
        console.warn('[AudioTab] micProcess fail:', err);
      });
  }, []);

  // 마이크 선택 시 프로세스 실행
  useEffect(() => {
    if (audioInputDeviceId) {
      micProcess(audioInputDeviceId);
    }
    return () => {
      if (javascriptNode && audioContext) {
        try {
          javascriptNode.disconnect(audioContext.destination);
          audioContext.close();
          audioContext = null;
        } catch (e) { /* ignore */ }
      }
    };
  }, [audioInputDeviceId, micProcess]);

  const handleSelectSpeaker = useCallback(
    (device: MediaDeviceInfo) => {
      if (onChangeDevice) {
        onChangeDevice('audiooutput', device.deviceId);
      } else {
        dispatch(localActions.setAudioOutputDeviceId(device.deviceId));
      }
      setIsSpeakerDropdownOpen(false);
    },
    [dispatch, onChangeDevice]
  );

  const handleSelectMic = useCallback(
    (device: MediaDeviceInfo) => {
      if (onChangeDevice) {
        onChangeDevice('audioinput', device.deviceId);
      } else {
        dispatch(localActions.setAudioInputDeviceId(device.deviceId));
      }
      setIsMicDropdownOpen(false);
    },
    [dispatch, onChangeDevice]
  );

  const handlePlaySample = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.volume = speakerVolume / 100;
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  }, [speakerVolume]);

  const handleChangeSpeakerVolume = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    dispatch(localActions.setSpeakerVolume(val));
    if (audioRef.current) {
      audioRef.current.volume = val / 100;
    }
  }, [dispatch]);

  const selectedSpeakerLabel =
    speakerDevices.find((d) => d.deviceId === audioOutputDeviceId)?.label || '스피커를 선택하세요';
  const selectedMicLabel =
    micDevices.find((d) => d.deviceId === audioInputDeviceId)?.label || '마이크를 선택하세요';

  return (
    <div className="rtcDialogSetupNew__audio">
      {/* 스피커 영역 */}
      <div className="rtcDialogSetupNew__audioItem">
        <div className="audioItem__title">
          <div className="audioItem__titleLeft">
            <strong>스피커</strong>
            <button
              type="button"
              className="common_round_btn sizeSmall3 notosans_font"
              onClick={handlePlaySample}
            >
              스피커 테스트
            </button>
            <audio ref={audioRef} src={testSound} style={{ display: 'none' }} />
          </div>
          <div className="audioItem__titleOpt" ref={speakerDropdownRef}>
            <div className={`rtc_newDropdown${isSpeakerDropdownOpen ? ' open' : ''}`}>
              <button
                type="button"
                className="rtc_newDropdown__btn"
                onClick={() => setIsSpeakerDropdownOpen((p) => !p)}
              >
                <span>{selectedSpeakerLabel}</span>
                <span className="lunacon lunacon--arrowDown-outlined" />
              </button>
              {isSpeakerDropdownOpen && (
                <ul className="rtc_newDropdown_list">
                  {speakerDevices.length === 0 ? (
                    <li>
                      <button type="button" className="rtc_newDropdown_listBtn" disabled>
                        사용 가능한 스피커 없음
                      </button>
                    </li>
                  ) : (
                    speakerDevices.map((device) => {
                      const isSelected = audioOutputDeviceId === device.deviceId;
                      return (
                        <li key={device.deviceId}>
                          <button
                            type="button"
                            className={'rtc_newDropdown_listBtn' + (isSelected ? ' selected' : '')}
                            onClick={() => handleSelectSpeaker(device)}
                          >
                            {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* 볼륨 슬라이더 */}
        <div className="audioItem__con">
          <div className="common_rangeSlider">
            <input
              type="range"
              className="common_rangeSliderInput"
              id="volume"
              min={0}
              max={100}
              value={speakerVolume}
              onChange={handleChangeSpeakerVolume}
              style={{
                background: `linear-gradient(to right, rgb(113, 155, 252) 0%, rgb(113, 155, 252) ${speakerVolume}%, rgb(216, 217, 221) ${speakerVolume}%, rgb(216, 217, 221) 100%)`,
              }}
            />
          </div>
        </div>
      </div>

      {/* 마이크 영역 */}
      <div className="rtcDialogSetupNew__audioItem">
        <div className="audioItem__title">
          <div className="audioItem__titleLeft">
            <strong>마이크</strong>
          </div>
          <div className="audioItem__titleOpt" ref={micDropdownRef}>
            <div className={`rtc_newDropdown${isMicDropdownOpen ? ' open' : ''}`}>
              <button
                type="button"
                className="rtc_newDropdown__btn"
                onClick={() => setIsMicDropdownOpen((p) => !p)}
              >
                <span>{selectedMicLabel}</span>
                <span className="lunacon lunacon--arrowDown-outlined" />
              </button>
              {isMicDropdownOpen && (
                <ul className="rtc_newDropdown_list">
                  {micDevices.length === 0 ? (
                    <li>
                      <button type="button" className="rtc_newDropdown_listBtn" disabled>
                        사용 가능한 마이크 없음
                      </button>
                    </li>
                  ) : (
                    micDevices.map((device) => {
                      const isSelected = audioInputDeviceId === device.deviceId;
                      return (
                        <li key={device.deviceId}>
                          <button
                            type="button"
                            className={'rtc_newDropdown_listBtn' + (isSelected ? ' selected' : '')}
                            onClick={() => handleSelectMic(device)}
                          >
                            {device.label || `Mic ${device.deviceId.slice(0, 8)}`}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* 마이크 레벨 & 설정 */}
        <div className="audioItem__con">
          <div
            className="common_micRangeSlider"
            style={{ '--bars': 88, '--level': micLevel } as React.CSSProperties}
          />

          <div className="audioItem__checkBox">
            <div className="common_radio_input type4">
              <label>
                <input
                  className="radio_input"
                  type="checkbox"
                  name="mute_on_join"
                  checked={isMuteOnJoin}
                  onChange={() => dispatch(localActions.setMuteOnJoin(!isMuteOnJoin))}
                />
                <span className="icon" />
                <span className="label_text">회의에 참가할 때 내 마이크 음소거</span>
              </label>
            </div>
          </div>
          <div className="audioItem__checkBox">
            <div className="common_radio_input type4">
              <label>
                <input
                  className="radio_input"
                  type="checkbox"
                  name="space_unmute"
                  checked={isSpaceUnmute}
                  onChange={() => dispatch(localActions.setSpaceUnmute(!isSpaceUnmute))}
                />
                <span className="icon" />
                <span className="label_text">Space키를 길게 눌러 내 음소거를 일시적으로 해제</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioTab;
